import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, CircularProgress, Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import {
    getEquipmentTypes, addEquipmentType, updateEquipmentType, deleteEquipmentType
} from '../api/admin';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const EquipmentManagement = () => {
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [actionType, setActionType] = useState('add'); 

    const fetchEquipment = async () => {
        try {
            setLoading(true);
            const data = await getEquipmentTypes();
            setEquipment(data.sort((a, b) => a.order - b.order));
        } catch (err) {
            setError(err.message || 'Failed to fetch equipment types.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEquipment();
    }, []);

    const onDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(equipment);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Optimistically update UI
        setEquipment(items);

        try {
            const token = localStorage.getItem('jwtToken');
            await axios.put(`${API_BASE_URL}/api/admin/equipment-types/reorder`, 
                { equipmentIds: items.map(i => i.id) }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            console.error(err);
            setError("Failed to save new order.");
            fetchEquipment(); // Revert on error
        }
    };

    const handleOpenAddDialog = () => {
        setActionType('add');
        setSelectedEquipment({ label: '', short: '' });
        setEditDialogOpen(true);
    };

    const handleOpenEditDialog = (item) => {
        setActionType('edit');
        setSelectedEquipment(item);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setSelectedEquipment(null);
    };

    const handleOpenConfirmDialog = (item) => {
        setSelectedEquipment(item);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
        setSelectedEquipment(null);
    };

    const handleSave = async () => {
        try {
            if (actionType === 'add') {
                await addEquipmentType(selectedEquipment);
            } else {
                await updateEquipmentType(selectedEquipment.id, selectedEquipment);
            }
            fetchEquipment(); 
            handleCloseEditDialog();
        } catch (err) {
            setError(`Failed to ${actionType} equipment type.`);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteEquipmentType(selectedEquipment.id);
            fetchEquipment(); 
            handleCloseConfirmDialog();
        } catch (err) {
            setError('Failed to delete equipment type.');
        }
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <StyledPaper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Manage Equipment Types (Drag to Reorder)</Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Add Equipment
                    </Button>
                </Box>
                <DragDropContext onDragEnd={onDragEnd}>
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: '#11224d' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', width: 50 }}></TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Label</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Short Code</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <Droppable droppableId="equipment-types">
                            {(provided) => (
                                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                                    {equipment.map((item, index) => (
                                        <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
                                            {(provided, snapshot) => (
                                                <TableRow
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    sx={{ 
                                                        ...provided.draggableProps.style,
                                                        backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'inherit',
                                                        display: snapshot.isDragging ? 'table' : 'table-row'
                                                    }}
                                                    hover
                                                >
                                    <TableCell {...provided.dragHandleProps}>
                                        <DragIndicatorIcon color="action" />
                                    </TableCell>
                                    <TableCell>{item.label}</TableCell>
                                    <TableCell>{item.short}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEditDialog(item)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleOpenConfirmDialog(item)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </TableBody>
                            )}
                        </Droppable>
                    </Table>
                </TableContainer>
                </DragDropContext>
            </StyledPaper>

            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} fullWidth maxWidth="xs">
                <DialogTitle>{actionType === 'add' ? 'Add New' : 'Edit'} Equipment Type</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Label (e.g., Dry Van)"
                        fullWidth
                        variant="outlined"
                        value={selectedEquipment?.label || ''}
                        onChange={(e) => setSelectedEquipment({ ...selectedEquipment, label: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Short Code (e.g., V)"
                        fullWidth
                        variant="outlined"
                        value={selectedEquipment?.short || ''}
                        onChange={(e) => setSelectedEquipment({ ...selectedEquipment, short: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            
            <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the equipment type "{selectedEquipment?.label}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EquipmentManagement;